import { useEffect, useState } from 'react';
import { FiSliders, FiWind, FiMapPin, FiCircle, FiHash, FiCrosshair, FiVolume2, FiClock } from 'react-icons/fi';
import { unlockThunder } from './thunderSound';
import { FaFire } from 'react-icons/fa';
import { WiBarometer } from 'react-icons/wi';
import { useShallow } from 'zustand/react/shallow';
import { useWeatherStore } from '../../store/weatherStore';
import { useTranslation } from '../../i18n/LanguageContext';
import { useLayerLabels } from '../../i18n/useLayerLabels';
import { getActiveBaseLayerKey } from '../../config/weatherLayers';

/** قائمة تراكبات خفيفة فوق الخريطة، منفصلة عن منتقي طبقات الطقس التفصيلي. */
export function OverlayQuickControls() {
    const { t } = useTranslation();
    const { layerName } = useLayerLabels();
    const [open, setOpen] = useState(false);
    const {
        visibleLayers, setActiveLayer, particlesEnabled, setParticlesEnabled,
        radarCoverageEnabled, setRadarCoverageEnabled,
        lightningSoundEnabled, setLightningSoundEnabled,
        timestampBadgeEnabled, setTimestampBadgeEnabled,
        mapLabelsEnabled, setMapLabelsEnabled,
        labelValuesEnabled, setLabelValuesEnabled,
        crosshairEnabled, setCrosshairEnabled,
        isobarsEnabled, setIsobarsEnabled,
        setSidebarOpen, layerControlsOpen, setLayerControlsOpen, setInfoPanelOpen, setSettingsOpen, setCycloneDockOpen,
    } = useWeatherStore(useShallow((s) => ({
        visibleLayers: s.visibleLayers,
        layerControlsOpen: s.layerControlsOpen,
        setActiveLayer: s.setActiveLayer,
        particlesEnabled: s.particlesEnabled,
        radarCoverageEnabled: s.radarCoverageEnabled,
        setRadarCoverageEnabled: s.setRadarCoverageEnabled,
        lightningSoundEnabled: s.lightningSoundEnabled,
        setTimestampBadgeEnabled: s.setTimestampBadgeEnabled,
        timestampBadgeEnabled: s.timestampBadgeEnabled,
        setLightningSoundEnabled: s.setLightningSoundEnabled,
        setParticlesEnabled: s.setParticlesEnabled,
        mapLabelsEnabled: s.mapLabelsEnabled,
        setMapLabelsEnabled: s.setMapLabelsEnabled,
        labelValuesEnabled: s.labelValuesEnabled,
        setLabelValuesEnabled: s.setLabelValuesEnabled,
        crosshairEnabled: s.crosshairEnabled,
        setCrosshairEnabled: s.setCrosshairEnabled,
        isobarsEnabled: s.isobarsEnabled,
        setIsobarsEnabled: s.setIsobarsEnabled,
        setSidebarOpen: s.setSidebarOpen,
        setLayerControlsOpen: s.setLayerControlsOpen,
        setInfoPanelOpen: s.setInfoPanelOpen,
        setSettingsOpen: s.setSettingsOpen,
        setCycloneDockOpen: s.setCycloneDockOpen,
    })));

    useEffect(() => {
        if (!open) return;
        const close = (event: PointerEvent) => {
            if (!(event.target as Element).closest('.overlay-quick')) setOpen(false);
        };
        document.addEventListener('pointerdown', close);
        return () => document.removeEventListener('pointerdown', close);
    }, [open]);

    useEffect(() => {
        const close = () => setOpen(false);
        window.addEventListener('rasid:close-overlay-quick', close);
        return () => window.removeEventListener('rasid:close-overlay-quick', close);
    }, []);

    // لا نسمح لقراءة قيمة الطقس العائمة أن تغطي قائمة التحكم المفتوحة.
    useEffect(() => {
        document.body.classList.toggle('overlay-quick-open', open);
        return () => document.body.classList.remove('overlay-quick-open');
    }, [open]);

    const toggleOpen = () => {
        if (!open) {
            setSidebarOpen(false);
            setLayerControlsOpen(false);
            setInfoPanelOpen(false);
            setSettingsOpen(false);
            setCycloneDockOpen(false);
        }
        setOpen((value) => !value);
    };

    const activeLayerKey = getActiveBaseLayerKey(visibleLayers);
    const isobarsOn = isobarsEnabled;

    const rows = [
        { id: 'labels', label: t('overlay.labels'), Icon: FiMapPin, on: mapLabelsEnabled, toggle: () => setMapLabelsEnabled(!mapLabelsEnabled) },
        // قيم المناطق — تابعةٌ لـ«المناطق» وتظهر تحتها وحدها. القيمة شارةٌ **بجوار اسم
        // المدينة**، فبلا أسماء لا مكان تُرسم فيه: مفتاحٌ ظاهرٌ وهي مُطفأة لا يغيّر شيئاً
        // على الخريطة، فيبدو معطَّلاً.
        //
        // ولا تُشترط بطبقةٍ سلَّمية: على الرادار والقمر يعود نوع العيّنة إلى الحرارة
        // (‏hoverType في NewWeatherMap)، فللشارة قيمةٌ حقيقية فوق كل الطبقات.
        ...(mapLabelsEnabled ? [{ id: 'label-values', label: t('overlay.labelValues'), Icon: FiHash, on: labelValuesEnabled, toggle: () => setLabelValuesEnabled(!labelValuesEnabled) }] : []),
        // الجسيمات: صفٌّ يغيب مع الرادار وحده — لا مفتاحَ لشيءٍ لا يُرسم (راجع
        // particlesOn في NewWeatherMap). والقيمة المخزَّنة تبقى كما تركها صاحبها،
        // فيجدها على حالها حين يعود إلى طبقةٍ ترسمها.
        ...(visibleLayers.radar ? [] : [{ id: 'particles', label: t('toggle.particles.label'), Icon: FiWind, on: particlesEnabled, toggle: () => setParticlesEnabled(!particlesEnabled) }]),
        { id: 'wildfires', label: layerName('wildfires'), Icon: FaFire, on: visibleLayers.wildfires, toggle: () => setActiveLayer('wildfires') },
        // تحذيرات الأرصاد: لا صفَّ لها — تعمل دائماً. التحذير إنذارُ سلامة، وإخفاؤه
        // خلف مفتاحٍ اختياري يجعل غيابه احتمالاً قائماً وقت الحاجة إليه.
        // الأعاصير ليست هنا: صارت بطاقةً في «خرائط الطقس» وخيارات عرضها تحتها.
        // قناع التغطية — مشروطٌ بطبقة الرادار وحدها: لا معنى لخيارٍ يضبط شيئاً غير
        // معروض. وهذا هو الصفّ الذي كان ناقصاً من القائمة.
        // خلفية الصور الجوّية: لا صفَّ لها — تعمل دائماً مع الرادار. وسقوطها إلى القاعدة
        // الرمادية عند نفاد حصّة Mapbox صامتٌ أصلاً، فلا حاجة لمفتاحٍ يدوي.
        // صوت الرعد — مع الطبقتين اللتين يُرسم فوقهما البرق المرصود فقط. والنقرة نفسها
        // تفتح سياق الصوت (unlockThunder): المتصفّح لا يسمح به إلا من لمسة مستخدم.
        ...(visibleLayers.radar || visibleLayers.satellite ? [{
            id: 'lightning-sound', label: t('overlay.lightningSound'), Icon: FiVolume2, on: lightningSoundEnabled,
            toggle: () => { if (!lightningSoundEnabled) unlockThunder(); setLightningSoundEnabled(!lightningSoundEnabled); },
        }] : []),
        ...(visibleLayers.radar ? [{ id: 'radar-coverage', label: t('overlay.radarCoverage'), Icon: FiCircle, on: radarCoverageEnabled, toggle: () => setRadarCoverageEnabled(!radarCoverageEnabled) }] : []),
        // البرق ليس هنا: مفتاحه يسكن تحت بطاقة الرادار/الأمطار في لوحة «خرائط الطقس»
        // — عند الطبقة التي يخصّها، كما تفعل رقائق «سرعة الرياح / هبّات الرياح».
        // خطوط الضغط متاحة فوق كل الطبقات بمفتاح عالمي واحد: ما يشغّله المستخدم يبقى
        // مشتغلاً وهو ينتقل بين الطبقات، وما يُطفئه يبقى مطفأً.
        // الشرط `activeLayerKey || radar` لا `activeLayerKey` وحده.
        //
        // ‏getActiveBaseLayerKey تُرجع الطبقة السلَّمية الفعّالة، والرادار **ليس منها**:
        // هو طبقة قاعدة قائمة بذاتها كالقمر. فكان الصفّ يختفي على الرادار بينما خطوط
        // الضغط **تُرسم فعلاً فوقه** — أي خطوطٌ ظاهرة بلا مفتاح يُطفئها، ولا سبيل إلى
        // إزالتها إلا بالخروج إلى طبقة أخرى وإطفائها هناك ثم العودة.
        ...(activeLayerKey || visibleLayers.radar ? [{ id: 'isobars', label: t('toggle.isobars.label'), Icon: WiBarometer, on: isobarsOn, toggle: () => setIsobarsEnabled(!isobarsOn) }] : []),
        // علامة التصويب في منتصف الشاشة — آخر القائمة كما في Zoom Earth: أداة عرض لا طبقة بيانات.
        { id: 'crosshair', label: t('overlay.crosshair'), Icon: FiCrosshair, on: crosshairEnabled, toggle: () => setCrosshairEnabled(!crosshairEnabled) },
        // ختم الوقت: بلا شرط طبقة — كل طبقة تُصوَّر وتُنشر، والصورة بلا تاريخ ناقصة.
        { id: 'timestamp', label: t('overlay.timestamp'), Icon: FiClock, on: timestampBadgeEnabled, toggle: () => setTimestampBadgeEnabled(!timestampBadgeEnabled) },
    ];

    // لوحة «خرائط الطقس» تُرسم في نفس الركن، فيظهر هذا الزرّ فوقها ملتصقاً بزرّ الإغلاق.
    // إخفاؤه ما دامت اللوحة مفتوحة يُنهي التداخل، ولا يخسر المستخدم شيئاً: اللوحة نفسها
    // تحوي التراكبات، ويعود الزرّ فور إغلاقها.
    if (layerControlsOpen) return null;

    return (
        <div className={`overlay-quick${open ? ' is-open' : ''}`}>
            {open && (
                <div className="overlay-quick-panel" role="menu" aria-label={t('layers.sectionOverlays')}>
                    <span className="overlay-quick-title">{t('layers.sectionOverlays')}</span>
                    {rows.map(({ id, label, Icon, on, toggle }) => (
                        <button key={id} data-overlay={id} className={`overlay-quick-row${on ? ' is-on' : ''}`} onClick={toggle} role="menuitemcheckbox" aria-checked={on}>
                            <span className="overlay-quick-label">{label}</span><Icon />
                        </button>
                    ))}
                </div>
            )}
            <button className="overlay-quick-fab" onClick={toggleOpen} aria-expanded={open} aria-label={t('layers.sectionOverlays')}>
                <FiSliders />
            </button>
        </div>
    );
}
